// src/components/ProjectExecution/AuditLogView.tsx
// EKPI-F1: Audit views and filters. Display only backend attribution; no frontend KPI/responsibility computation.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  TablePagination,
} from '@mui/material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getProjectAuditLogs } from '../../services/projectApi';
import type {
  AuditLogItem,
  AuditActorType,
  AuditEntityType,
  AuditKpiImpact,
  AuditLogsQueryParams,
} from '../../types';

const ACTOR_OPTIONS: { value: '' | AuditActorType; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'TENANT', label: 'Tenant' },
  { value: 'FRAPORT_INTERNAL', label: 'Fraport internal' },
  { value: 'CLIENT', label: 'Client' },
];

const KPI_IMPACT_OPTIONS: { value: '' | AuditKpiImpact; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'POSITIVE', label: 'Positive' },
  { value: 'NEUTRAL', label: 'Neutral' },
  { value: 'NEGATIVE', label: 'Negative' },
];

const ENTITY_TYPE_OPTIONS: { value: '' | AuditEntityType; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'PROJECT', label: 'Project' },
  { value: 'PHASE', label: 'Phase' },
  { value: 'TASK', label: 'Task' },
  { value: 'ACTION', label: 'Action' },
];

/** User-friendly label for event type (display only; backend sends verbatim). */
function eventTypeLabel(eventType: string): string {
  const labels: Record<string, string> = {
    PROJECT_LOCKED: 'Project locked',
    PROJECT_UNLOCKED: 'Project unlocked',
    PROJECT_ADVANCE_DAY: 'Project advance day',
    PROJECT_COMPLETED: 'Project completed',
    HIERARCHY_ACTUAL_START_SET: 'Phase / Task / Action started',
    PHASE_ACTUAL_START_SET: 'Phase actual start set',
    PHASE_ACTUAL_END_SET: 'Phase actual end set',
    TASK_ACTUAL_START_SET: 'Task actual start set',
    TASK_ACTUAL_END_SET: 'Task actual end set',
    ACTION_ACTUAL_START_SET: 'Action actual start set',
    ACTION_ACTUAL_END_SET: 'Action actual end set',
    ACTION_REASSIGNED: 'Action reassigned',
    ACTION_DELAY_DETECTED: 'Action delay detected',
  };
  return labels[eventType] ?? eventType;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return value;
  }
}

function kpiImpactColor(impact: AuditKpiImpact): 'success' | 'default' | 'error' {
  if (impact === 'POSITIVE') return 'success';
  if (impact === 'NEGATIVE') return 'error';
  return 'default';
}

const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 500;
const EXPORT_PAGE_SIZE = 200;

interface AuditLogViewProps {
  projectId: number;
}

export const AuditLogView: React.FC<AuditLogViewProps> = ({ projectId }) => {
  const [items, setItems] = useState<AuditLogItem[]>([]);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  const [actorType, setActorType] = useState<'' | AuditActorType>('');
  const [kpiImpact, setKpiImpact] = useState<'' | AuditKpiImpact>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [entityType, setEntityType] = useState<'' | AuditEntityType>('');
  const [entityId, setEntityId] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params: AuditLogsQueryParams = {
      limit: Math.min(Math.max(1, limit), MAX_LIMIT),
      offset,
    };
    if (actorType) params.actorType = actorType;
    if (kpiImpact) params.kpiImpact = kpiImpact;
    if (from) params.from = from;
    if (to) params.to = to;
    if (entityType) params.entityType = entityType;
    const eid = entityId.trim() ? parseInt(entityId, 10) : undefined;
    if (eid != null && !Number.isNaN(eid)) params.entityId = eid;

    try {
      const res = await getProjectAuditLogs(projectId, params);
      setItems(res.items);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Failed to load audit logs';
      setError(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, limit, offset, actorType, kpiImpact, from, to, entityType, entityId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handlePageChange = (_: unknown, page: number) => {
    setOffset(page * limit);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = Math.min(MAX_LIMIT, Math.max(1, parseInt(e.target.value, 10) || DEFAULT_LIMIT));
    setLimit(newLimit);
    setOffset(0);
  };

  const handleExportPdf = useCallback(async () => {
    setExportingPdf(true);
    setError(null);
    const params: AuditLogsQueryParams = {
      limit: EXPORT_PAGE_SIZE,
      offset: 0,
    };
    if (actorType) params.actorType = actorType;
    if (kpiImpact) params.kpiImpact = kpiImpact;
    if (from) params.from = from;
    if (to) params.to = to;
    if (entityType) params.entityType = entityType;
    const eid = entityId.trim() ? parseInt(entityId, 10) : undefined;
    if (eid != null && !Number.isNaN(eid)) params.entityId = eid;

    try {
      const allItems: AuditLogItem[] = [];
      let currentOffset = 0;
      let batch: AuditLogItem[];
      do {
        const res = await getProjectAuditLogs(projectId, { ...params, offset: currentOffset });
        batch = res.items;
        allItems.push(...batch);
        currentOffset += EXPORT_PAGE_SIZE;
      } while (batch.length === EXPORT_PAGE_SIZE);

      const projectName = allItems.length > 0 && allItems[0].projectName
        ? allItems[0].projectName
        : `Project ${projectId}`;
      const filterParts: string[] = [];
      if (actorType) filterParts.push(`Actor: ${ACTOR_OPTIONS.find((o) => o.value === actorType)?.label ?? actorType}`);
      if (kpiImpact) filterParts.push(`KPI: ${kpiImpact}`);
      if (from || to) filterParts.push(`Date: ${from || '…'} to ${to || '…'}`);
      if (entityType) filterParts.push(`Entity: ${entityType}`);
      if (eid != null && !Number.isNaN(eid)) filterParts.push(`Entity ID: ${eid}`);
      const filterSummary = filterParts.length > 0 ? filterParts.join('; ') : 'No filters';

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      doc.setFontSize(10);
      doc.text(`Execution KPI Audit Log – ${projectName}`, 14, 10);
      doc.text(`Exported: ${new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`, 14, 16);
      doc.text(`Filters: ${filterSummary}`, 14, 22);

      const headers = [
        'Time (UTC)',
        'Event',
        'Entity',
        'Actor',
        'KPI impact',
        'Project date',
        'Planned start',
        'Planned end',
        'Actual start',
        'Actual end',
        'Source document',
        'Notes',
      ];
      const rows = allItems.map((row) => {
        const entity =
          row.projectName ?? row.phaseName ?? row.taskName
            ? [
                row.projectName && `Project: ${row.projectName}`,
                row.phaseName && `Phase: ${row.phaseName}`,
                row.taskName && `Task: ${row.taskName}`,
                row.entityType === 'ACTION' && row.actionType
                  ? `ACTION: #${row.entityId} (${row.actionType.charAt(0) + row.actionType.slice(1).toLowerCase()})`
                  : `${row.entityType}: #${row.entityId}`,
              ]
                .filter(Boolean)
                .join(', ')
            : row.entityType === 'ACTION' && row.actionType
              ? `${row.entityType} #${row.entityId} (${row.actionType.charAt(0) + row.actionType.slice(1).toLowerCase()})`
              : `${row.entityType} #${row.entityId}`;
        return [
          formatDate(row.timestampUtc),
          eventTypeLabel(row.eventType),
          entity,
          row.actorDisplayName ?? `${row.actorType} (user ${row.actorUserId})`,
          row.kpiImpact,
          row.projectDate || '—',
          row.plannedStart || '—',
          row.plannedEnd || '—',
          row.actualStart || '—',
          row.actualEnd || '—',
          row.entityType === 'ACTION' ? (row.sourceDocumentTitle ?? '—') : '—',
          row.notes ?? '—',
        ];
      });

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 28,
        styles: { fontSize: 7 },
        headStyles: { fontSize: 7 },
        margin: { left: 14, right: 14 },
      });

      doc.save(`audit-log-${projectId}-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : 'Failed to export audit log to PDF';
      setError(msg);
    } finally {
      setExportingPdf(false);
    }
  }, [projectId, actorType, kpiImpact, from, to, entityType, entityId]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Execution KPI Audit Log
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Attribution (actor, KPI impact) is provided by the backend. This view displays only what the API returns.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Filters</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Actor type</InputLabel>
            <Select
              value={actorType}
              label="Actor type"
              onChange={(e) => setActorType(e.target.value as '' | AuditActorType)}
            >
              {ACTOR_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>KPI impact</InputLabel>
            <Select
              value={kpiImpact}
              label="KPI impact"
              onChange={(e) => setKpiImpact(e.target.value as '' | AuditKpiImpact)}
            >
              {KPI_IMPACT_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="From (YYYY-MM-DD)"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <TextField
            size="small"
            label="To (YYYY-MM-DD)"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 180 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Entity type</InputLabel>
            <Select
              value={entityType}
              label="Entity type"
              onChange={(e) => setEntityType(e.target.value as '' | AuditEntityType)}
            >
              {ENTITY_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Entity ID"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            placeholder="e.g. action ID"
            type="number"
            sx={{ width: 120 }}
          />
          <Button variant="contained" onClick={fetchLogs} disabled={loading}>
            Apply
          </Button>
          <Button
            variant="outlined"
            onClick={handleExportPdf}
            disabled={loading || exportingPdf}
          >
            {exportingPdf ? 'Exporting…' : 'Export to PDF'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Time (UTC)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Event</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Entity</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actor</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>KPI impact</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Project date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Planned start</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Planned end</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actual start</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actual end</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Source document</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                      No audit log entries match the current filters.
                    </TableCell>
                  </TableRow>
                ) : (
                    items.map((row) => (
                    <TableRow key={row.logId}>
                      <TableCell>{formatDate(row.timestampUtc)}</TableCell>
                      <TableCell>{eventTypeLabel(row.eventType)}</TableCell>
                      <TableCell>
                        {row.projectName ?? row.phaseName ?? row.taskName
                          ? [
                              row.projectName && `Project: ${row.projectName}`,
                              row.phaseName && `Phase: ${row.phaseName}`,
                              row.taskName && `Task: ${row.taskName}`,
                              row.entityType === 'ACTION' && row.actionType
                                ? `ACTION: #${row.entityId} (${row.actionType.charAt(0) + row.actionType.slice(1).toLowerCase()})`
                                : `${row.entityType}: #${row.entityId}`,
                            ].filter(Boolean).join(', ')
                          : row.entityType === 'ACTION' && row.actionType
                            ? `${row.entityType} #${row.entityId} (${row.actionType.charAt(0) + row.actionType.slice(1).toLowerCase()})`
                            : `${row.entityType} #${row.entityId}`}
                      </TableCell>
                      <TableCell>{row.actorDisplayName ?? `${row.actorType} (user ${row.actorUserId})`}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.kpiImpact} color={kpiImpactColor(row.kpiImpact)} />
                      </TableCell>
                      <TableCell>{row.projectDate || '—'}</TableCell>
                      <TableCell>{row.plannedStart || '—'}</TableCell>
                      <TableCell>{row.plannedEnd || '—'}</TableCell>
                      <TableCell>{row.actualStart || '—'}</TableCell>
                      <TableCell>{row.actualEnd || '—'}</TableCell>
                      <TableCell>{row.entityType === 'ACTION' ? (row.sourceDocumentTitle ?? '—') : '—'}</TableCell>
                      <TableCell>{row.notes ?? '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={-1}
            page={Math.floor(offset / limit)}
            onPageChange={handlePageChange}
            rowsPerPage={limit}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[25, 50, 100, 250]}
            labelRowsPerPage="Rows per page:"
            backIconButtonProps={{ disabled: offset === 0 }}
            nextIconButtonProps={{ disabled: items.length < limit }}
          />
        </>
      )}
    </Box>
  );
};
